import CancelIcon from "@mui/icons-material/Cancel"
import { Grid, IconButton, MenuItem, TextField } from "@mui/material"
import { getAnalytics, logEvent } from "firebase/analytics"
import { FunctionComponent, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { timber_debug } from "../Timber"
import { Inventory, InvoiceItem } from "../Type"
import { getEmptyInventory } from "../contexts/InventoryContext"
import { formatCurrencyAmount } from "../utils/CurrencyUtils"

const getInventoryByName = (
  inventoryName: string,
  inventories: Inventory[]
): Inventory => {
  // No need to execute for loop below if id is empty
  if (inventoryName === "") {
    timber_debug("Inventory name empty")
    return getEmptyInventory()
  }

  for (let inventory of inventories) {
    if (inventory.name === inventoryName) {
      // timber_debug("Found inventory")
      // timber_debug(inventory)
      return inventory
    }
  }

  // Probably deleted by user
  timber_debug("Inventory deleted by user")
  return getEmptyInventory()
}

const InvoiceItemView: FunctionComponent<{
  inventories: Inventory[]
  invoiceItem: InvoiceItem
  onRemoveInvoiceItem: (invoiceItem: InvoiceItem) => void
  onUpdateTotalPrice: () => void
}> = ({
  inventories,
  invoiceItem: item,
  onRemoveInvoiceItem,
  onUpdateTotalPrice,
}) => {
  const intl = useIntl()
  const analytics = getAnalytics()

  const [invoiceItem, setInvoiceItem] = useState(item)
  const [quantity, setQuantity] = useState<number | string>(item.quantity)
  const [selectedInventory, setSelectedInventory] = useState<Inventory>(
    getInventoryByName(invoiceItem.name, inventories)
  )

  useEffect(() => {
    setInvoiceItem(item)
    setQuantity(item.quantity)
    setSelectedInventory(getInventoryByName(invoiceItem.name, inventories))
    item.totalPrice = selectedInventory.unitPrice * (quantity as number)
    return () => {}
  }, [invoiceItem])

  return (
    <Grid container spacing={2}>
      {/* Inventory dropdown list */}
      <Grid size={{ sm: 5, md: 5, lg: 5 }}>
        <TextField
          fullWidth
          select
          label={intl.formatMessage({
            id: "dialog.invoice.inventories",
          })}
          value={selectedInventory.name}
          onChange={(event: any) => {
            const inventory = getInventoryByName(
              event.target.value,
              inventories
            )
            setSelectedInventory(inventory)
            item.name = inventory.name
            item.unitPrice = inventory.unitPrice
            item.totalPrice = inventory.unitPrice * (quantity as number)
            onUpdateTotalPrice()
            logEvent(analytics, "web_invoice_item_selected")
          }}
        >
          {inventories.map((inventory, index) => (
            <MenuItem key={index} value={inventory.name}>
              {inventory.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Invoice item quantity */}
      <Grid size={{ sm: 3, md: 3, lg: 3 }}>
        <TextField
          fullWidth
          type="number"
          label={intl.formatMessage({
            id: "table.headers.quantity",
          })}
          value={quantity}
          onChange={(newValue: any) => {
            if (newValue.target.value !== "") {
              setQuantity(parseInt(newValue.target.value))
              item.quantity = parseInt(newValue.target.value)
              item.totalPrice = selectedInventory.unitPrice * item.quantity
            } else {
              setQuantity("")
            }
            onUpdateTotalPrice()
          }}
        ></TextField>
      </Grid>

      {/* Invoice item price = inventory unit price * quantity */}
      <Grid size={{ sm: 3, md: 3, lg: 3 }}>
        <TextField
          disabled
          fullWidth
          type="number"
          label={intl.formatMessage({
            id: "table.headers.price",
          })}
          value={formatCurrencyAmount(
            (quantity as number) * selectedInventory.unitPrice
          )}
        ></TextField>
      </Grid>

      {/* Delete invoice item */}
      <Grid size={{ sm: 1, md: 1, lg: 1 }}>
        <IconButton
          color="primary"
          aria-label="upload picture"
          onClick={() => onRemoveInvoiceItem(invoiceItem)}
        >
          <CancelIcon />
        </IconButton>
      </Grid>
    </Grid>
  )
}

export default InvoiceItemView
